import { useState, useRef } from 'react';
import Modal from './Modal';
import { Map } from '../Map';
import {
  workflowActionsClass,
  workflowFieldClass,
  workflowPrimaryClass,
  workflowSecondaryClass,
} from '../WorkflowForm';
import { Upload, X } from 'lucide-react';

interface RaceModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit?: (raceData: RaceData) => Promise<void>;
  existingRace?: RaceData;
  disabled?: boolean;
  isSubmitting?: boolean;
}

export interface RaceData {
  id?: number;
  name: string;
  date: string;
  distance: string;
  url: string;
  description?: string;
  gpx_file_url?: string;
  gpx_file?: File;
}

export default function RaceModal({
  show = false,
  onClose,
  onSubmit,
  existingRace,
  disabled = false,
  isSubmitting = false,
}: RaceModalProps) {
  const [raceData, setRaceData] = useState<RaceData>({
    id: existingRace?.id || undefined,
    name: existingRace?.name || '',
    date: existingRace?.date || '',
    distance: existingRace?.distance || '',
    url: existingRace?.url || '',
    description: existingRace?.description || '',
    gpx_file_url: existingRace?.gpx_file_url || '',
  });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (onSubmit) {
      await onSubmit(raceData);
    }
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.gpx')) {
        setUploadError('Please select a valid GPX file.');
        return;
      }

      // Clear any previous errors
      setUploadError(null);

      setRaceData({ ...raceData, gpx_file: file });
    }
  };

  const handleRemoveFile = () => {
    setRaceData({ ...raceData, gpx_file: undefined });
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={existingRace ? 'Edit Race' : 'Add New Race'}
    >
      <div className="flex flex-col h-full">
        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
          <fieldset
            disabled={disabled || isSubmitting}
            className="space-y-4 flex-1"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Race Name
              </label>
              <input
                type="text"
                required
                 className={workflowFieldClass}
                value={raceData.name}
                onChange={(e) =>
                  setRaceData({ ...raceData, name: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                required
                 className={workflowFieldClass}
                value={raceData.date}
                onChange={(e) =>
                  setRaceData({ ...raceData, date: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Distance
              </label>
              <select
                required
                 className={workflowFieldClass}
                value={raceData.distance}
                onChange={(e) =>
                  setRaceData({ ...raceData, distance: e.target.value })
                }
              >
                <option value="">Select a distance</option>
                <option value="5k">5k</option>
                <option value="10k">10k</option>
                <option value="half marathon">Half Marathon</option>
                <option value="marathon">Marathon</option>
                <option value="ultra marathon">Ultra Marathon</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Official Race Website
              </label>
              <input
                type="url"
                required
                 className={workflowFieldClass}
                value={raceData.url}
                onChange={(e) =>
                  setRaceData({ ...raceData, url: e.target.value })
                }
                placeholder="https://example.com/race"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                  className={`${workflowFieldClass} min-h-28 resize-none`}
                rows={3}
                value={raceData.description}
                onChange={(e) =>
                  setRaceData({ ...raceData, description: e.target.value })
                }
                placeholder="Optional race description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GPX Route File (Optional)
              </label>
              <div className="space-y-3">
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".gpx"
                  onChange={handleFileChange}
                  className="hidden"
                />

                {/* Upload UI */}
                {!raceData.gpx_file ? (
                  <div
                    onClick={handleUploadClick}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 cursor-pointer transition-colors"
                  >
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                      {raceData.gpx_file_url
                        ? 'Click to upload new GPX file (will replace current)'
                        : 'Click to upload GPX file'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      GPX files only (max 10MB)
                    </p>
                    {raceData.gpx_file_url ? (
                      <p className="text-xs text-brand-cobalt mt-2">
                        Current: Race has existing GPX file
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="w-full border border-gray-300 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <Upload className="h-8 w-8 text-brand-green" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {raceData.gpx_file
                              ? raceData.gpx_file.name
                              : 'GPX file uploaded'}
                          </p>
                          {raceData.gpx_file ? (
                            <p className="text-xs text-gray-500">
                              {(raceData.gpx_file.size / 1024).toFixed(2)} KB
                            </p>
                          ) : null}
                        </div>
                      </div>
                      {raceData.gpx_file ? (
               <button
                          type="button"
                          onClick={handleRemoveFile}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}

                {uploadError ? (
                  <div className="text-sm text-red-600">{uploadError}</div>
                ) : null}

                {raceData.gpx_file_url ? (
                  <div className="mt-2">
                    <Map url={raceData.gpx_file_url} />
                  </div>
                ) : null}
              </div>
            </div>
          </fieldset>

          {/* Always show buttons unless explicitly disabled (admin read-only mode) */}
          {!disabled ? (
            <div className={workflowActionsClass}>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                 className={workflowSecondaryClass}
              >
                Cancel
              </button>
               <button
                type="submit"
                disabled={isSubmitting}
                  className={workflowPrimaryClass}
              >
                {isSubmitting
                  ? 'Creating...'
                  : existingRace
                    ? 'Update'
                    : 'Add Race'}
              </button>
            </div>
          ) : null}
        </form>
      </div>
    </Modal>
  );
}
